import React, { useEffect, useState } from 'react';
import styles from './AnamneseHistory.module.css';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import CreateAnamnesePopupContent from '../../../../popup/CreateAnamnesePopupContent/CreateAnamnesePopupContent';
import AnamneseInfo from './anamneseInfo/AnamneseInfo';
import DateHistory from './dateHistory/DateHistory';
import { DataProviderInstance } from '../../../../api/DataProvider';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildAnamnesisJob } from '../../modules/reportSections';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.ANAMNESIS];

/**
 * `appointmentId` liga a anamnese nova ao atendimento em curso; `anamnesisTemplateId`
 * é o modelo fixado no tipo de atendimento (config.anamnesis.templateId). Com ele o
 * botão vira "Preencher <modelo>"; se este atendimento já tem anamnese, vira "Revisar".
 * `patientName` e `professionalId` identificam o PDF da anamnese exibida.
 */
const AnamneseHistory = ({ patientId, currentDate, list, enabled, onUpdated, collapsible = false, appointmentId = null, anamnesisTemplateId = null, patientName, professionalId }) => {
  const { CompanyConfig } = DataProviderInstance();
  const fixedTemplate = anamnesisTemplateId
    ? (CompanyConfig?.AnamneseTemplates || []).find((template) => String(template.Id) === String(anamnesisTemplateId)) || null
    : null;
  const appointmentAnamnese = appointmentId
    ? list.find((item) => item.appointmentId != null && String(item.appointmentId) === String(appointmentId)) || null
    : null;
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [currentAnamneseIndex, setCurrentAnamneseIndex] = useState(0);
  const [currentAnamnese, setCurrentAnamnese] = useState(null);
  const [editingAnamnese, setEditingAnamnese] = useState(null);

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig: CompanyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildAnamnesisJob(currentAnamnese, CompanyConfig),
  });

  useEffect(() => {
    updateCurrentAnamnese(currentAnamneseIndex);
  }, [currentAnamneseIndex, list]);

  const updateCurrentAnamnese = (index) => {
    if (list.length > 0) {
      setCurrentAnamnese(list[index]);
    }
  }

  useEffect(() => {
    if(list.length == 0) {
      setCurrentAnamneseIndex(0);
      setCurrentAnamnese(null);
    }
  }, [list]);

  useEffect(() => {
    if (editingAnamnese) {
      showPopup();
    }
  }, [editingAnamnese]);

  function onCreateAnamneseHandler() {
    showPopup();
  }

  function onReviewAnamneseHandler() {
    const index = list.indexOf(appointmentAnamnese);
    if (index >= 0) setCurrentAnamneseIndex(index);
    setEditingAnamnese(appointmentAnamnese);
  }

  function renderActionButton() {
    if (appointmentAnamnese) {
      return (
        <CustomButton
          style="primary"
          variant="outlined"
          icon={<EditOutlinedIcon />}
          label="Revisar anamnese"
          onClick={onReviewAnamneseHandler}
        />
      );
    }
    return (
      <CustomButton
        style="primary"
        variant="contained"
        icon={<AddOutlinedIcon />}
        label={fixedTemplate ? `Preencher ${fixedTemplate.Label}` : 'Nova anamnese'}
        enabled={enabled}
        onClick={onCreateAnamneseHandler}
      />
    );
  }

  function showPopup() {
    setShowCreatePopup(true);
  }

  function handleEditAnamnese() {
    setEditingAnamnese(currentAnamnese);
  }

  function onCreatedAnamneseHandler() {
    if (onUpdated) {
      onUpdated();
    }
  }

  function onItemSelectHandler(index) {
    setCurrentAnamneseIndex(index);
  }

  return (
    <>
      <ModuleCard
        accent="purple"
        title="Anamnese"
        icon={<HistoryEduOutlinedIcon />}
        description={`Histórico de anamneses realizadas (${list.length})`}
        actions={(
          <>
            {currentAnamnese && <ModulePdfButton report={report} />}
            {enabled && renderActionButton()}
          </>
        )}
        collapsible={collapsible}
      >
        {list.length == 0 ? (
          <ModuleEmptyState icon={<HistoryEduOutlinedIcon />} text="Não existem anamneses registradas." />
        ) : (
          <>
            {list.length > 1 && (
              <div className={styles.dateHistoryContainer}>
                {list.map((item, index) => (
                  <DateHistory
                    key={index}
                    id={index}
                    date={item.createdAt}
                    onSelect={onItemSelectHandler}
                    actived={index == currentAnamneseIndex}
                  />
                ))}
              </div>
            )}

            {currentAnamnese && (
              <AnamneseInfo
                data={currentAnamnese}
                onEdit={enabled ? handleEditAnamnese : null}
                isCurrent={Boolean(appointmentAnamnese) && currentAnamnese === appointmentAnamnese}
              />
            )}
          </>
        )}
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {showCreatePopup && (
        <CreateAnamnesePopupContent
          currentDate={currentDate}
          patientId={patientId}
          appointmentId={appointmentId}
          fixedTemplateId={fixedTemplate ? fixedTemplate.Id : null}
          OnSave={onCreatedAnamneseHandler}
          OnHide={() => {
            setShowCreatePopup(false);
            setEditingAnamnese(null);
          }}
          anamnese={editingAnamnese}
        />
      )}
    </>
  );
};

export default AnamneseHistory;
